/**
 * Context de Autenticação
 * Gerencia o estado global de autenticação da aplicação
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authService from '../services/authService';
import { clearAllCourseCaches } from '../config/queryClient';
import {
  ACCESS_DENIED_EVENT,
  SESSION_EXPIRED_EVENT,
  accessDeniedMessage,
  isCourseSelectionResponse,
} from '../services/authService';
import type { AccessDeniedDetail } from '../services/authService';
import type {
  User,
  LoginCredentials,
  CourseSelectionResponse,
} from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  courseSelection: CourseSelectionResponse | null;
  /**
   * Por que a sessão terminou, quando foi o backend que negou o acesso fora de
   * um curso (conta bloqueada, prazo vencido em todos). A tela de login mostra.
   */
  notice: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  selectCourse: (userId: string) => Promise<void>;
  switchCourse: (userId: string) => Promise<void>;
  cancelCourseSelection: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [courseSelection, setCourseSelection] = useState<CourseSelectionResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // AuthProvider fica dentro do QueryClientProvider (App.tsx), então o client
  // está disponível aqui.
  const queryClient = useQueryClient();

  // Verifica autenticação ao carregar
  useEffect(() => {
    checkAuth();
  }, []);

  // Redireciona para login quando a sessão expira durante uso da aplicação
  useEffect(() => {
    const handleSessionExpired = () => {
      setToken(null);
      setUser(null);
      setCourseSelection(null);
    };

    const handleAccessDenied = (event: Event) => {
      handleSessionExpired();
      setNotice(accessDeniedMessage((event as CustomEvent<AccessDeniedDetail>).detail));
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
    };
  }, []);

  /**
   * Descarta o cache ao fim de uma sessão — o cache é por usuário, e sem isso
   * entrar com outra conta na mesma aba poderia servir dados da anterior.
   *
   * Fica num efeito, e não dentro de logout()/handleSessionExpired(), para
   * rodar depois do commit que desmonta as telas do curso. Limpar durante o
   * handler deixaria observers ainda montados sem dados, e eles refariam as
   * requisições sem token — 401 que dispararia SESSION_EXPIRED de novo. Cobre
   * os dois caminhos de saída (logout explícito e token expirado) num lugar só.
   */
  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    const isAuth = !!token && !!user;

    if (wasAuthenticatedRef.current && !isAuth) {
      queryClient.clear();
      // Cada curso tem o próprio cache, então o clear() acima alcançaria só a
      // vitrine.
      clearAllCourseCaches();
    }

    wasAuthenticatedRef.current = isAuth;
  }, [token, user, queryClient]);

  const checkAuth = () => {
    setIsLoading(true);
    try {
      const isValid = authService.isAuthenticated();
      
      if (isValid) {
        const storedToken = authService.getStoredToken();
        const storedUser = authService.getStoredUser();
        
        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
        }
      } else {
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      authService.logout();
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setNotice(null);
    try {
      const response = await authService.login(credentials);

      if (!isCourseSelectionResponse(response)) {
        setToken(response.access_token);
        setUser(response.user);
        setCourseSelection(null);
        return;
      }

      // Servidor ainda na versão que parava numa tela de seleção. Ninguém
      // escolhe curso antes de entrar: quem escolhe é a vitrine, com os cards e
      // o progresso. A sessão começa no curso pedido (quando veio um) ou na
      // primeira matrícula ativa — o curso da sessão não decide mais nada, já
      // que cada requisição manda o seu no header `X-Course-Id`.
      //
      // Isto existe para o intervalo entre o deploy do backend e o deste app;
      // com o backend novo o login já devolve o token direto e nada aqui roda.
      const target =
        credentials.topicId != null
          ? response.courses.find(
              (c) => c.topicId === credentials.topicId && !c.isBlocked,
            )
          : response.courses.find((c) => !c.isBlocked);

      if (!target) {
        // Nada utilizável (curso pedido bloqueado, ou todas as matrículas
        // suspensas): a tela de seleção é o único lugar que sabe dizer por quê,
        // curso a curso.
        setCourseSelection(response);
        return;
      }

      const authenticated = await authService.selectCourse(
        response.selection_token,
        target.userId,
      );
      setToken(authenticated.access_token);
      setUser(authenticated.user);
      setCourseSelection(null);
    } catch (error) {
      if (error instanceof Error && error.message.includes('expirada')) {
        logout();
      }
      throw error;
    }
  };

  const selectCourse = async (userId: string): Promise<void> => {
    if (!courseSelection) {
      throw new Error('Nenhuma seleção de curso em andamento');
    }

    try {
      const response = await authService.selectCourse(
        courseSelection.selection_token,
        userId,
      );
      setToken(response.access_token);
      setUser(response.user);
      setCourseSelection(null);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Troca o curso ativo.
   *
   * Antes isto pedia um token novo ao servidor e descartava o cache inteiro,
   * porque o curso era a matrícula que o token apontava. Agora o curso é o
   * header de cada requisição e as chaves de cache são por curso, então trocar
   * é só navegar — os dados do curso anterior continuam válidos e voltar para
   * ele é instantâneo.
   *
   * A função permanece para o caso de o servidor ainda estar na versão antiga
   * durante um deploy: ali o token precisa mesmo ser reemitido.
   *
   * @deprecated Navegue para `/curso/<slug>`.
   */
  const switchCourse = async (userId: string): Promise<void> => {
    const response = await authService.switchCourse(userId);

    setToken(response.access_token);
    setUser(response.user);
    setCourseSelection(null);
  };

  const cancelCourseSelection = () => {
    setCourseSelection(null);
  };

  const logout = () => {
    authService.logout();
    setToken(null);
    setUser(null);
    setCourseSelection(null);
    // O cache é descartado pelo efeito de fim de sessão acima, depois que as
    // telas do curso desmontarem.
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    courseSelection,
    notice,
    login,
    selectCourse,
    switchCourse,
    cancelCourseSelection,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook para usar o contexto de autenticação
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
